//codex-- Keep the toolbar redesign scoped to pages in this folder.
import PageToolbar from './PageToolbar';
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import DateFormat from '../components/DateFormat';
import SubMenu from '../components/SubMenu';
import StageAddItemModal from '../components/StageAddItemModal';
import { TrashIcon } from '@heroicons/react/24/outline';
import StageReleaseItemModal from '../components/StageReleaseItemModal';
import StageEditItemNotesModal from '../components/StageEditItemNotesModal';
import StageDeliverReleaseModal from '../components/StageDeliverReleaseModal';

const StageDetailPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [releases, setReleases] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);

  const [releaseModalOpen, setReleaseModalOpen] = useState(false);
  const [releaseItem, setReleaseItem] = useState(null); // the row selected

  // add near other modals
  const [editNotesOpen, setEditNotesOpen] = useState(false);
  const [editNotesItem, setEditNotesItem] = useState(null);

  const [deliverOpen, setDeliverOpen] = useState(false);
  const [deliverRelease, setDeliverRelease] = useState(null);  

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // open modal
  const openEditNotes = (row) => {
    setEditNotesItem(row);
    setEditNotesOpen(true);
  };  

  const handleOpenDeliver = (rel) => {
    setDeliverRelease(rel);
    setDeliverOpen(true);
  };  

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/inventory_crud.php?staging_id=${id}`);
      // console.log('Stage Detail Response:', res.data);
      if (Array.isArray(res.data) && res.data[0] === 'success') {
        const payload = res.data[1] || {};
        setHeader(payload.header || null);
        setItems(payload.items || []);
        setReleases(payload.releases || []);
      } else if (res.data?.error) {
        toast.error(res.data.error);
      } else {
        toast.error('Failed to load stage');
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
        navigate('/login');
      } else {
        toast.error('API error loading stage');
      }
    } finally {
      setLoading(false);
    }
  };

  const totalQty = useMemo(
    () => items.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0),
    [items]
  );

  const getItemMenu = (row) => [
    // Placeholder for future actions (edit / delete / releases)
    // { name: 'Edit Item', click: () => {} },
  ];

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this staged item?')) return;

    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        {
          status: 'deleteStagingItem',
          data: { id: itemId }
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const ok = (res.data && res.data.success === true)
        || (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        toast.success('Item deleted');
        fetchDetail();
      } else {
        toast.error(res.data?.error || 'Failed to delete item');
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
        navigate('/login');
      } else {
        toast.error('API error deleting item');
      }
    }
  };

  const releasedByItem = useMemo(() => {
    const map = new Map();
    releases.forEach(r => {
      const sid = Number(r.stage_item_id);
      const prev = map.get(sid) || 0;
      map.set(sid, prev + Number(r.released_quantity || 0));
    });
    return map;
  }, [releases]);

  const handleDeleteRelease = async (releaseId) => {
    if (!window.confirm('Delete this release? This should also revert inventory.')) return;
    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        { status: 'deleteStagingRelease', data: { id: releaseId } },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const ok = (res.data && res.data.success === true) || (Array.isArray(res.data) && res.data[0] === 'success');
      if (ok) {
        toast.success('Release deleted');
        fetchDetail();
      } else {
        toast.error(res.data?.error || 'Failed to delete release');
      }
    } catch (e) {
      toast.error('API error deleting release');
    }
  };  

const visibleReleases = useMemo(
  () => (releases || []).filter(r => (r.status || '').toLowerCase() !== 'deliveredx'),
  [releases]
);


  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="float-right mb-4">
        <button
          className="text-blue-500 hover:underline"
          onClick={() => navigate('/inventory_dashboard', { state: { tab: 'Stage' } })}
        >
          &larr; Back Stage List
        </button>
      </div>
      <h1 className="text-xl font-bold">Stage #{id}</h1>
      <br/>
      <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
        <PageToolbar title="Stage tools" actions={<>
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded" onClick={() => setShowAddItem(true)}>+ Add Item</button>
        </>} />
      </>

      {/* Header Card */}
      {loading ? (
        <p>Loading…</p>
      ) : !header ? (
        <div className="p-4 border rounded bg-red-50 text-red-700">Stage not found.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 border rounded bg-white">
              <div className="text-sm text-gray-500">Identifier</div>
              <div className="font-semibold">
                {header.job_number ? `Job: ${header.job_number}` : `Service Log: ${header.service_log_number}`}
              </div>
            </div>
            <div className="p-4 border rounded bg-white">
              <div className="text-sm text-gray-500">Stage Date</div>
              <div className="font-semibold"><DateFormat date={header.stage_date} /></div>
            </div>
            <div className="p-4 border rounded bg-white">
              <div className="text-sm text-gray-500">Status</div>
              <span className={`
                inline-block px-2 py-1 rounded-full text-xs font-semibold
                ${header.status === 'Released'   ? 'bg-yellow-200 text-yellow-800' : ''}
                ${header.status === 'Delivered'  ? 'bg-green-200 text-green-800' : ''}
                ${header.status === 'Cancelled'  ? 'bg-red-200 text-red-800'     : ''}
                ${header.status === 'Open'       ? 'bg-gray-200 text-gray-700'   : ''}
              `}>
                {header.status}
              </span>
            </div>
            <div className="md:col-span-3 p-4 border rounded bg-white">
              <div className="text-sm text-gray-500 mb-1">Notes</div>
              <div className="text-sm whitespace-pre-line">{header.notes || '—'}</div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold">Staged Items</h2>
              <div className="text-sm text-gray-600">Total Qty: <span className="font-semibold">{totalQty}</span></div>
            </div>
            <table className="min-w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-200">
                <tr>
                  <th className="border px-3 py-2 text-left">Item</th>
                  <th className="border px-3 py-2 text-left">Description</th>
                  <th className="border px-3 py-2">Qty</th>
                  <th className="border px-3 py-2">From Location</th>
                  <th className="border px-3 py-2 text-left">Notes</th>
                  <th className="border px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-6 text-gray-400">No items staged yet.</td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <tr key={row.id} className="hover:bg-indigo-50">
                      <td className="border px-3 py-2 text-sm">
                        <div className="font-medium">{row.device_name || `#${row.device_id}`}</div>
                        <div className="text-xs text-gray-500">{row.manufacture || ''}</div>
                      </td>
                      <td className="border px-3 py-2 text-sm">{row.full_description || '—'}</td>
                      <td className="border px-3 py-2 text-center text-sm">{row.quantity}</td>
                      <td className="border px-3 py-2 text-center text-sm">{row.from_location_id}</td>
                      <td className="border px-3 py-2 text-sm">{row.notes || '—'}</td>
                      <td className="border px-3 py-2 text-center">                        
                        <SubMenu
                            label="Actions"
                            idItem={row.id}
                            links={[
                              { name: 'Release', click: () => { setReleaseItem(row); setReleaseModalOpen(true); } },
                              { name: 'Edit Notes', click: () => openEditNotes(row) },
                              // { name: 'Delete Item', click: () => handleDeleteItem(row.id) }, // commented out per request
                            ]}
                          />

                        {/* Old delete icon (commented out)
                        <button
                          className="text-red-600 hover:text-red-800"
                          title="Delete Item"
                          onClick={() => handleDeleteItem(row.id)}
                        >
                          <TrashIcon className="w-5 h-5 inline-block" />
                        </button>
                        */}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Releases Table (optional, useful context) */}
          <div className="mb-8">
            <h2 className="font-semibold mb-2">Releases</h2>
            <table className="min-w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-200">
                <tr>
                  <th className="border px-3 py-2">R-ID</th>
                  <th className="border px-3 py-2">Item/ID</th>
                  <th className="border px-3 py-2">Qty</th>
                  <th className="border px-3 py-2">Released</th>
                  <th className="border px-3 py-2">By</th>
                  <th className="border px-3 py-2">Delivered</th>
                  <th className="border px-3 py-2">Status</th>
                  <th className="border px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {visibleReleases.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-6 text-gray-400">No releases yet.</td>
                  </tr>
                ) : (
                  visibleReleases.map(r => (
                    <tr key={r.id} className={`hover:bg-indigo-50 ${r.status === 'Delivered' ? 'bg-green-50' : ''}`}>
                      <td className="border px-3 py-2 text-sm">
                      {r.status === 'Delivered' ? ( <span className="text-green-600 px-3 py-2">✔</span> ) : (                        
                        <SubMenu
                          label={`#${r.id}`}
                          idItem={r.id}
                          links={[
                            ...(r.status === 'In Transit'
                              ? [{ name: 'Mark Delivered', click: () => handleOpenDeliver(r) }]
                              : []),
                            ...(r.status === 'Delivered'
                              ? [] // hide delete if delivered
                              : [{ name: 'Delete Release', click: () => handleDeleteRelease(r.id) }]),
                          ]}
                        />
                        )}
                      </td>
                      <td className="border px-3 py-2 text-sm">
                        {r.device_name ? `${r.device_name} ` : ''}#{r.stage_item_id}
                      </td>
                      <td className="border px-3 py-2 text-center text-sm">{r.released_quantity}</td>
                      <td className="border px-3 py-2 text-sm"><DateFormat date={r.release_date} /></td>
                      <td className="border px-3 py-2 text-sm">{r.released_by}</td>
                      <td className="border px-3 py-2 text-sm">
                        {r.delivered_date ? <DateFormat date={r.delivered_date} /> : '—'}
                      </td>
                      <td className="border px-3 py-2 text-sm">{r.status}</td>
                      <td className="border px-3 py-2 text-sm">{r.notes || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add Item Modal */}
      {showAddItem && header && (
        <StageAddItemModal
          apiUrl={apiUrl}
          stageId={header.id}
          onClose={() => setShowAddItem(false)}
          onSaved={() => { setShowAddItem(false); fetchDetail(); }}
        />
      )}

      {releaseModalOpen && releaseItem && (
        <StageReleaseItemModal
          apiUrl={apiUrl}
          item={{
            ...releaseItem,
            _released: Number(releasedByItem.get(Number(releaseItem.id)) || 0),
          }}
          maxQty={Math.max(
            0,
            Number(releaseItem.quantity || 0) - Number(releasedByItem.get(Number(releaseItem.id)) || 0)
          )}
          onClose={() => { setReleaseItem(null); setReleaseModalOpen(false); }}
          onReleased={() => fetchDetail()}
        />
      )}

      {editNotesOpen && editNotesItem && (
        <StageEditItemNotesModal
          apiUrl={apiUrl}
          item={editNotesItem}
          onClose={() => { setEditNotesOpen(false); setEditNotesItem(null); }}
          onSaved={() => fetchDetail()}
        />
      )}

      {deliverOpen && deliverRelease && (
        <StageDeliverReleaseModal
          apiUrl={apiUrl}
          release={deliverRelease}
          onClose={() => { setDeliverOpen(false); setDeliverRelease(null); }}
          onDelivered={() => fetchDetail()}
        />
      )}      
    </div>
  );
};

export default StageDetailPage;
